import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type AppRole = "user" | "admin" | "super_admin";

export function useRole() {
  const { user, loading: authLoading } = useAuth();
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);

  // Depend on the user's id, not the whole `user` object — see the matching
  // comment in useProfile.ts. Avoids a needless refetch on every Supabase
  // token refresh (same user, new object reference).
  const userId = user?.id;
  useEffect(() => {
    if (authLoading) return;
    if (!userId) {
      setRoles([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("user_roles" as any)
          .select("role")
          .eq("user_id", userId);
        if (cancelled) return;
        if (error) {
          setRoles([]);
        } else {
          setRoles(((data ?? []) as any[]).map((r) => r.role as AppRole));
        }
      } catch {
        if (!cancelled) setRoles([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, authLoading]);

  const isAdmin = roles.includes("admin") || roles.includes("super_admin");
  const isSuperAdmin = roles.includes("super_admin");

  return { roles, isAdmin, isSuperAdmin, loading: authLoading || loading };
}
